import type { Components, Theme } from '@mui/material/styles'
import { alpha } from '@mui/material/styles'

export const chipControlStyles = (
    theme: Theme,
    backgroundColor = 'transparent',
    highlightedBackgroundColor = theme.palette.action.hover,
) => ({
    ...theme.typography.body2,
    minHeight: theme.sizing.control.regular,
    paddingBlock: theme.spacing(0.5),
    boxSizing: 'border-box' as const,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor,
    '&:is(label)': {
        cursor: 'pointer',
        '&[aria-disabled="true"]': {
            cursor: 'not-allowed',
        },
    },
    '&:hover:not(:disabled):not(.Mui-disabled):not([aria-disabled="true"])': {
        backgroundColor: highlightedBackgroundColor,
    },
    '&.Mui-focusVisible': {
        backgroundColor,
    },
    '&:focus-visible': {
        outline: `1px dotted ${theme.palette.text.secondary}`,
        outlineOffset: 2,
    },
})

export const chipFilterControlStyles = (theme: Theme) => ({
    ...chipControlStyles(theme),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(0.5),
    gap: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    justifyContent: 'flex-start',
})

export const chipFilterClearButtonStyles = (theme: Theme) => ({
    width: theme.sizing.control.compact,
    height: theme.sizing.control.compact,
    padding: theme.spacing(0.25),
    flexShrink: 0,
    '& .MuiSvgIcon-root': {
        fontSize: theme.typography.body2.fontSize,
        opacity: 0.55,
    },
})

export const switchChipLabelStyles = (theme: Theme, label: string) => ({
    ...theme.typography.body2,
    content: JSON.stringify(label),
    position: 'absolute',
    inset: theme.spacing(0, 0.75, 0, 4),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.primary,
    pointerEvents: 'none',
})

export const chipTheme: Components<Theme> = {
    MuiChip: {
        styleOverrides: {
            root: ({ theme, ownerState }) => ({
                fontSize: theme.typography.body2.fontSize,
                ...(ownerState.color === 'default' ? {
                    '&.Mui-focusVisible': {
                        backgroundColor: ownerState.variant === 'outlined' ? 'transparent' : theme.palette.action.selected,
                    },
                    '&[aria-pressed="true"]': {
                        borderColor: theme.palette.text.secondary,
                        backgroundColor: theme.palette.action.selected,
                        '&:hover': {
                            backgroundColor: alpha(theme.palette.text.primary, theme.palette.action.selectedOpacity + theme.palette.action.hoverOpacity),
                        },
                        '&.Mui-focusVisible': {
                            backgroundColor: theme.palette.action.selected,
                        },
                    },
                } : {}),
                '&:focus-visible': {
                    outline: `1px dotted ${theme.palette.text.secondary}`,
                    outlineOffset: 2,
                },
            }),
            outlined: ({ theme, ownerState }) => ownerState.color === 'default' ? { borderColor: theme.palette.divider } : {},
            sizeSmall: ({ theme }) => ({ fontSize: theme.typography.caption.fontSize }),
        },
    },
}